import { useMemo, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import {
  DOCS_GET_STARTED,
  DOCS_HOME,
  DOCS_PRIVACY,
  DOCS_TOS,
} from 'constants/Docs.constants';
import type { PopupActions } from 'reactjs-popup/dist/types';
import { PATHS } from 'routes/routes.constants';

import { useRouter } from '@polyai/common/hooks/useRouter';
import IconButton from '@polyai/ui/components/atoms/IconButton';
import {
  CaretDownIcon,
  CatalogIcon,
  HelpIcon,
  LogoutIcon,
  UserIcon,
} from '@polyai/ui/components/atoms/Icons';
import Logo from '@polyai/ui/components/atoms/Logo';
import {
  PopupMenu,
  PopupMenuItem,
} from '@polyai/ui/components/molecules/PopupMenu';

import AccountModal from 'components/molecules/AccountModal';
import { useAppSelector } from 'hooks/reduxHooks';
import {
  GLOBAL_PERMISSION,
  useGlobalPermissions,
} from 'hooks/useProjectResources/useGlobalPermissions';
import useProjectRoute from 'hooks/useProjectRoute';
import { getActionName } from 'lib/ddlogHelper';
import * as MainNavStyled from '../../organisms/ProjectNavigation/ProjectNavigation.styled';
import * as Styled from './LandingNav.styled';

const LandingNav = () => {
  const { accountId } = useProjectRoute();

  const logout = useAppSelector(state => state.auth.logout);

  const { user } = useAuth0();
  const { pushUrl } = useRouter();
  const { userHasGlobalPermission } = useGlobalPermissions();

  const accountMenuRef = useRef<PopupActions>(null);
  const helpMenuRef = useRef<PopupActions>(null);

  const closePopupWithCallback = (callback: () => void) => {
    accountMenuRef.current?.close();

    callback();
  };

  const userName = useMemo(() => {
    if (user?.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }

    if (user?.given_name) {
      return user.given_name;
    }

    return user?.name ?? '';
  }, [user]);

  return (
    <Styled.LandingNavWrapper>
      <MainNavStyled.LogoWrapper data-test-id="back-to-home-page-button">
        <MainNavStyled.LogoLink to={`/${accountId ?? ''}`}>
          <Logo />
        </MainNavStyled.LogoLink>
      </MainNavStyled.LogoWrapper>
      <Styled.DropdownWrapper>
        <Styled.AccountActionsMenu>
          <PopupMenu
            contentStyle={{ width: 'auto', marginTop: '8px' }}
            offsetY={8}
            popupRef={accountMenuRef}
            position="bottom right"
            size="sm"
            trigger={
              <Styled.AccountActionsMenuButton
                data-test-id="menu-popup-button"
                hoverTargetColor="backgroundPrimaryHover"
                hoverTarget
              >
                <Styled.UserName>{userName}</Styled.UserName>
                <CaretDownIcon size="lg" />
              </Styled.AccountActionsMenuButton>
            }
            nested
          >
            <AccountModal
              trigger={
                <Styled.PopupMenuButton
                  onClick={() => closePopupWithCallback(logout as () => void)}
                >
                  <UserIcon color="iconSecondaryInverse" />
                  <span>Account</span>
                </Styled.PopupMenuButton>
              }
            />
            {userHasGlobalPermission(GLOBAL_PERMISSION.PLATFORM_ADMIN) && (
              <Styled.PopupMenuButton
                onClick={() =>
                  closePopupWithCallback(() => {
                    pushUrl(PATHS.ADMIN_PAGE);
                  })
                }
              >
                <CatalogIcon color="iconSecondaryInverse" />
                <span>Feature flags</span>
              </Styled.PopupMenuButton>
            )}
            <Styled.PopupMenuButton
              onClick={() => closePopupWithCallback(logout as () => void)}
            >
              <LogoutIcon color="iconSecondaryInverse" />
              <span>Log out</span>
            </Styled.PopupMenuButton>
          </PopupMenu>
        </Styled.AccountActionsMenu>

        <PopupMenu
          contentStyle={{ width: 'auto', marginTop: '8px' }}
          offsetY={8}
          popupRef={helpMenuRef}
          position="bottom right"
          size="sm"
          trigger={
            <IconButton hoverTargetColor="backgroundPrimaryHover" hoverTarget>
              <HelpIcon color="iconPrimaryInverse" />
            </IconButton>
          }
          nested
        >
          <PopupMenuItem
            data-dd-action={getActionName('Help', 'open', 'Getting Started')}
            forwardedAs="a"
            href={DOCS_GET_STARTED}
            target="_blank"
          >
            Getting Started
          </PopupMenuItem>

          <PopupMenuItem
            data-dd-action={getActionName('Help', 'open', 'Help Centre')}
            forwardedAs="a"
            href={DOCS_HOME}
            target="_blank"
          >
            Help Center
          </PopupMenuItem>

          <PopupMenuItem
            data-dd-action={getActionName('Help', 'open', 'Terms and Service')}
            forwardedAs="a"
            href={DOCS_TOS}
            target="_blank"
          >
            Terms and Service
          </PopupMenuItem>

          <PopupMenuItem
            data-dd-action={getActionName('Help', 'open', 'Privacy Policy')}
            forwardedAs="a"
            href={DOCS_PRIVACY}
            target="_blank"
          >
            Privacy Policy
          </PopupMenuItem>
        </PopupMenu>
      </Styled.DropdownWrapper>
    </Styled.LandingNavWrapper>
  );
};

export default LandingNav;
